import { css } from '@emotion/react';
import { memo, ReactElement, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import ReactSelect, { ValueType } from 'react-select';
import { ChatAttributeClassifier } from 'sdk';

import { commonMessages } from '../../../../i18n/commonMessages';
import { styled } from '../../../../styled';
import { Theme } from '../../../../theme/interface/detailed';
import { isMobile } from '../../../../utils/isMobile';
import { SelectInitialValueType } from '../field';

type SelectInterface = {
	className: string;
	name: string;
	initialValue?: SelectInitialValueType;
	attributes: ChatAttributeClassifier[];
	isMulti?: boolean;
	hasError: boolean;
};

type OptionType = { value: string; label: string };

const transformEntries = (entries: ChatAttributeClassifier[] | ChatAttributeClassifier): OptionType[] | OptionType =>
	entries instanceof Array
		? entries.map(({ title, key }) => ({ value: key, label: title }))
		: { value: entries.key, label: entries.title };

const Component = ({ className, name, attributes, initialValue, isMulti = false }: SelectInterface): ReactElement => {
	const { options, initValue } = useMemo(() => {
		const options = transformEntries(attributes) as OptionType[];
		const initValue = initialValue && transformEntries(initialValue);
		return { options, initValue };
	}, [attributes, initialValue]);

	const { formatMessage } = useIntl();

	const [selectedOptions, setSelectedOptions] = useState<ValueType<OptionType, boolean>>(initValue);
	useEffect(() => {
		setSelectedOptions(initValue);
	}, [initValue]);
	return (
		<ReactSelect
			options={options}
			name={name}
			isMulti={isMulti}
			isSearchable={!isMobile}
			value={selectedOptions}
			onChange={setSelectedOptions}
			classNamePrefix="select"
			className={className}
			placeholder={formatMessage(commonMessages.select) + '...'}
			menuPosition="absolute"
			components={useMemo(
				() => ({
					IndicatorSeparator: (): null => null,
				}),
				[]
			)}
		/>
	);
};

export const Select = styled(memo(Component))(
	({
		theme: {
			allSettings: {
				fields: { common },
				select: { option, multiselectSelectedOption },
			},
		},
		hasError,
	}: {
		theme: Theme;
	} & SelectInterface) =>
		css`
			margin-bottom: ${common.marginBottom};

			.select__control {
				background-color: ${common.background};
				border: ${hasError ? '2px solid ' + common.errorColor : common.border};
				border-radius: ${common.borderRadius};
				box-shadow: 0 0 0 0;
				box-sizing: border-box;
				flex-wrap: wrap;
				min-height: initial;
				min-height: 40px;
				padding: 0 10px;
				width: 100%;

				& * {
					font-weight: inherit;
					padding: 0;
				}

				&:hover {
					border: ${hasError ? '2px solid ' + common.errorColor : common.border};
				}

				&--is-focused {
					border: ${common.onFocusBorder};

					&:hover {
						border: ${common.onFocusBorder};
					}
				}
			}

			.select__control input {
				position: ${isMobile ? 'absolute' : 'relative'};
			}

			.select__value-container {
				min-height: 23px;
				overflow: visible;
				padding: 5px 0;
			}

			.select__indicator {
				svg {
					height: 16px;
					width: 17px;
				}
			}

			.select__option {
				background-color: ${option.backgroundColor};
				box-sizing: border-box;
				color: ${option.color};
				font-family: ${option.fontFamily};
				font-size: ${option.fontSize};
				word-break: break-word;

				&:hover {
					background-color: ${option.hoverBackgroundColor};
				}
			}

			select__menu {
				overflow: visible;
			}

			.select__multi-value {
				background-color: ${multiselectSelectedOption.backgroundColor};
				border-radius: 2px;
				font-family: ${multiselectSelectedOption.fontFamily};
				min-height: 22px;

				&__label {
					align-items: center;
					border-radius: 2px;
					display: flex;
					white-space: normal;
					word-break: break-word;
					${multiselectSelectedOption}
				}

				&__remove {
					display: flex;
					justify-content: center;

					svg {
						fill: #717d83;
						height: 15px;
						padding: 0 6px;
						width: 15px;
					}
				}
			}

			.select__single-value,
			.select__placeholder {
				color: ${common.color};
				font-family: ${option.fontFamily};
				font-size: ${common.fontSize};
				margin: 0px;
				position: static;
				transform: none;
				white-space: normal;
				word-break: break-word;
			}
		`
);
